// Job Template Item editor and stage-scoped catalogue writes.
import { inject, Injector } from '@angular/core';
import { ActivityEntityName, JobTemplateCode, JobTemplateItemCommandId } from '@jms/model';
import { resolveItemFromRow } from '@workbuddy/entities/shared/items';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { mergeEntityBoardConfig } from '@workbuddy/services/entities/merge.entity.board.config';
import type { EntityManifestBoardProfile } from '@workbuddy/services/entities/entity.manifest.surface';
import { createEmbeddedItemAddAdapter, embeddedItemBoardProfile, loadEmbeddedItemCatalogueFacts, lockedEmbeddedItemReadonly, type EmbeddedItemProfile } from '@workbuddy/surfaces/embedded-item';
import type { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';
import { lastValueFrom } from 'rxjs';
import { JOB_TEMPLATE_ITEM_QUERY_KEY, JOB_TEMPLATE_ITEM_SCOPE, type JobTemplateItemBoardParams } from '@workbuddy/entities/work.job.template.item/job.template.item.model';

export function jobTemplateItemBoardProfile(_base: EntityManifestBoardProfile, page: EntityBoardContext): Partial<EntityManifestBoardProfile> {

  const editor = {
    entity: ActivityEntityName.JobTemplateItem,
    lineEntity: ActivityEntityName.JobTemplateCode,
    scope: JOB_TEMPLATE_ITEM_SCOPE,
    queryKey: JOB_TEMPLATE_ITEM_QUERY_KEY,

    // No long description, status or payable: none of these exists on a template line.
    fields: {},
    layout: () => 'stacked',

    // The row is the line: the board projects every field the editor shows, so no per-item read exists.
    load: async (row, params, injector) => {
      const line = resolveItemFromRow(row, JOB_TEMPLATE_ITEM_SCOPE) as JobTemplateCode | null;
      if (!line?._id || !line.name) return null;

      const facts = await loadEmbeddedItemCatalogueFacts(line.name, injector);
      return {
        line,
        uoms: facts.uoms,
        readonly: lockedEmbeddedItemReadonly(!params?.writable(), facts.canOverrideDesc),

        // Hidden entirely when the catalogue forbids overriding, matching legacy — an empty box the write
        // would reject is nothing the user can act on.
        hide: facts.canOverrideDesc ? {} : { description: true },
      };
    },

    // Nothing reprices on the client: an absent price is the instruction for a generated job to resolve
    // it from the template's pricebook, which is what legacy's toggle-off meant.
    priceReset: 'server',

    // `JobTemplateStageCodeRepository.patch` is a plain write with no rule of its own.
    serverZeroesForcedPrices: false,

    save: async (line, patch, params, injector) => {
      const stageId = params?.stage()?._id;
      if (!stageId) throw new Error('The owning stage is required to save this item.');
      await lastValueFrom(injector.get(EntityDataServiceRaw).executeCommand(ActivityEntityName.JobTemplateItem, JobTemplateItemCommandId.UpdateItem, { stageId, itemId: line._id, patch }));
    },

    afterWrite: params => params?.itemsChanged(),

    // 56, not the 40px default: the money cell stacks the extension over the unit price, which a 40px
    // row crowds. Same fixed height the SOR board uses for the same cell.
    board: {
      rowHeight: 56,

      // One stage-scoped list; switching or favouriting a view does nothing here.
      // Title stays, same as Tasks — without it the header is only search + Add.
      header: { showTitle: true, showViewOptions: false },
    },

    // No last-item guard, unlike the recurring-job board — nothing generates from a job template on a
    // schedule, so an empty stage is a legitimate state while building one.
    delete: {
      confirm: line => ({ title: 'Delete item', message: `Delete '${line.name}'? Jobs already generated keep the items they were created with.` }),
      write: async (line, params, injector) => {
        const stageId = params?.stage()?._id;
        if (!stageId) return;
        await lastValueFrom(injector.get(EntityDataServiceRaw).executeCommand(ActivityEntityName.JobTemplateItem, JobTemplateItemCommandId.DeleteItem, { stageId, itemId: line._id }));
      },
    },
  } satisfies EmbeddedItemProfile<JobTemplateCode, JobTemplateItemBoardParams>;

  const entityBoard = embeddedItemBoardProfile(editor);

  const params = page.params as JobTemplateItemBoardParams;
  const injector = inject(Injector);
  const entityData = inject(EntityDataServiceRaw);

  if (!params) return entityBoard;

  return mergeEntityBoardConfig(entityBoard, {
    header: {
      hideAdd: !page.header?.parentId || !params.writable(),
    },
    params: {
      itemBrowser: () => createEmbeddedItemAddAdapter({
        title: 'Browse Items',
        canAdd: () => !!params.stage()?._id && params.writable(),
        write: lines => {
          const stageId = params.stage()?._id;
          if (!stageId) throw new Error('A stage is required to add items.');

          return lastValueFrom(entityData.executeCommand<JobTemplateCode[]>(
            ActivityEntityName.JobTemplateItem,
            JobTemplateItemCommandId.AddItems,
            { stageId, items: lines },
          ));
        },
        queryKey: JOB_TEMPLATE_ITEM_QUERY_KEY,
      }, injector),
    },
  }) as Partial<EntityManifestBoardProfile>;
}
